import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, ChevronDown, FileText, Search, Send, Sparkles, Loader2, ArrowLeft, MessageSquare, Files, Eye, EyeOff, Maximize2, X, CheckCircle, Brain, Database, Zap } from "lucide-react";
import { normalizeCaseData } from "../utils/caseHelpers";
import { sendStreamingChatQueryV2, fetchCaseById, fetchSignedUrl, fetchCaseRuns } from "../api";

/**
 * ThinkingSteps Component - Shows AI reasoning progress
 * Supports collapsing and sub-items display
 */
const ThinkingSteps = ({ steps, isCollapsed = false }) => {
  const [expanded, setExpanded] = useState(!isCollapsed);

  // Auto-collapse when isCollapsed prop changes
  useEffect(() => {
    if (isCollapsed) setExpanded(false);
  }, [isCollapsed]);

  if (!steps || steps.length === 0) return null;

  const completedCount = steps.filter(s => s.status === 'completed').length;
  const totalCount = steps.length;

  // When collapsed, show a summary
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs dark:bg-slate-800/50 bg-slate-100 rounded-full border dark:border-slate-700/50 border-slate-200 dark:text-slate-400 text-slate-500 hover:dark:bg-slate-800 hover:bg-slate-200 transition-colors"
      >
        <div className="p-0.5 rounded-full dark:bg-yellow-500/20 bg-yellow-500/10">
          <Brain className="w-3 h-3 text-yellow-500" />
        </div>
        <span>
          {completedCount === totalCount
            ? `Analyzed in ${totalCount} steps`
            : `${completedCount}/${totalCount} steps completed`
          }
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
    );
  }

  return (
    <div className="p-3 dark:bg-slate-800/50 bg-slate-100 rounded-xl border dark:border-slate-700/50 border-slate-200 animate-fade-in-up">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-md dark:bg-yellow-500/20 bg-yellow-500/10">
            <Brain className="w-3.5 h-3.5 text-yellow-500" />
          </div>
          <span className="text-xs font-medium dark:text-slate-400 text-slate-500 uppercase tracking-wider">
            AI Reasoning
          </span>
        </div>
        <button
          onClick={() => setExpanded(false)}
          className="p-1 rounded hover:dark:bg-slate-700 hover:bg-slate-200 dark:text-slate-400 text-slate-500 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="space-y-1.5">
        {steps.map((step, index) => {
          const isCompleted = step.status === 'completed';
          const isError = step.status === 'error';
          const isInProgress = step.status === 'in_progress';

          return (
            <div key={step.stepId} className="animate-fade-in-up">
              {/* Main step row */}
              <div
                className={`flex items-center gap-2 py-1 text-sm transition-all ${isCompleted ? 'opacity-70' : 'opacity-100'
                  }`}
              >
                {/* Status indicator */}
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  {isCompleted ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : isError ? (
                    <X className="w-4 h-4 text-red-500" />
                  ) : (
                    <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                  )}
                </div>

                {/* Step title */}
                <span className={`flex-1 ${isCompleted
                  ? 'dark:text-slate-400 text-slate-500'
                  : isError
                    ? 'dark:text-red-400 text-red-600'
                    : 'dark:text-slate-200 text-slate-700'
                  }`}>
                  {step.title}
                </span>
              </div>

              {/* Sub-items (shown for in-progress or if there are items) */}
              {step.items && step.items.length > 0 && (
                <div className="ml-6 mt-1 space-y-0.5">
                  {step.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="text-xs dark:text-slate-500 text-slate-400 pl-2 border-l-2 dark:border-slate-700 border-slate-300 py-0.5 truncate"
                      title={item}
                    >
                      {item.length > 60 ? `"${item.substring(0, 60)}..."` : `"${item}"`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatLoanAmount = (value) => {
  const amount = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  if (!amount) return "Not set";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Parse message content to extract thinking blocks and action buttons
 * Returns: { cleanContent, thinkingContent, showThinkingCollapsed, actionButtons }
 */
const parseMessageContent = (content) => {
  if (!content) return { cleanContent: '', thinkingContent: null, showThinkingCollapsed: false, actionButtons: [] };

  // Extract <thinking>...</thinking> blocks
  const thinkingRegex = /<thinking>([\s\S]*?)<\/thinking>/gi;
  const thinkingMatches = content.match(thinkingRegex);
  const thinkingContent = thinkingMatches
    ? thinkingMatches.map(m => m.replace(/<\/?thinking>/gi, '').trim()).join('\n')
    : null;

  // Remove thinking blocks from content
  let cleanContent = content.replace(thinkingRegex, '').trim();

  // If cleanContent is empty but we have thinking content, use thinking as main content
  // This handles cases where the AI puts everything in <thinking> tags
  let showThinkingCollapsed = true;
  if (!cleanContent && thinkingContent) {
    cleanContent = thinkingContent;
    showThinkingCollapsed = false; // Don't show collapsed section since we're using it as main content
  }

  // Detect action buttons from common patterns
  const actionButtons = [];
  const textToAnalyze = cleanContent.toLowerCase();

  // Pattern 1: "Basic (500 credits) or Advanced (2,000 credits)" - extract options
  const orPattern = /(\w+(?:\s+\([^)]+\))?)\s+or\s+(\w+(?:\s+\([^)]+\))?)\s+report\??/i;
  const orMatch = cleanContent.match(orPattern);
  if (orMatch) {
    actionButtons.push(orMatch[1].trim());
    actionButtons.push(orMatch[2].trim());
  }

  // Pattern 2: Confirmation prompts - "Confirm?" at end of message
  const confirmPattern = /\bconfirm\??\s*$/i;
  if (confirmPattern.test(cleanContent) && actionButtons.length === 0) {
    actionButtons.push('Yes, proceed');
    actionButtons.push('Cancel');
  }

  // Pattern 3: Waiting for confirmation/proceed
  if (actionButtons.length === 0 &&
      (textToAnalyze.includes('proceed with') ||
       textToAnalyze.includes('wait for') ||
       textToAnalyze.includes('explicit confirmation') ||
       textToAnalyze.includes('would you like to'))) {
    actionButtons.push('Proceed');
    actionButtons.push('Cancel');
  }

  // Pattern 4: Yes/No questions
  const yesNoPattern = /\?\s*$/;
  if (yesNoPattern.test(cleanContent) && actionButtons.length === 0 &&
      (textToAnalyze.includes('would you like') ||
       textToAnalyze.includes('do you want') ||
       textToAnalyze.includes('shall i'))) {
    actionButtons.push('Yes');
    actionButtons.push('No');
  }

  return { cleanContent, thinkingContent: showThinkingCollapsed ? thinkingContent : null, showThinkingCollapsed, actionButtons };
};

const promptSuggestions = [
  "Summarize this case in 5 bullet points.",
  "What documents are still missing?",
  "Highlight underwriting risks I should flag.",
  "Draft a borrower update email.",
];

const realEstateJokes = [
  "Waiting for the underwriter... (They're basically a ninja: quick, precise, and occasionally heartbreaking).",
  "Why did the house go to the doctor? It had a window pane.",
  "What kind of building weighs the least? A lighthouse.",
  "Why was the mortgage lender always calm? Because they had a lot of interest in remaining stable.",
  "This is taking a moment... just like closing day.",
  "Calculating debt-to-income ratios... and how many coffees you need today.",
  "Why did the scarecrow win an award? He was outstanding in his field (and had great curb appeal).",
  "What do you call a real estate agent who sells haunted houses? A paranormal realtor.",
  "Why did the hipster burn his tongue? He drank his coffee before it was cool... just like that hot market.",
  "I told my carpenter I didn't want carpet on the steps. He gave me a blank stare.",
  "Why did the homeowner install a skylight? The overhead costs were through the roof.",
  "My real estate agent told me to stage the house. Now there’s a drum kit in the living room.",
  "Why don’t buildings ever fall down? Because they have great support systems.",
  "I asked the developer for a discount. He said he couldn't structure that deal.",
  "Why did the brick break up with the mortar? There was no bond left.",
  "A skeleton walked into an open house. The agent said, 'Glad you could make it, I know you have nobody to go with.'",
  "This response is loading faster than a title search, I promise.",
  "Checking thecomps... and double-checking the coffee pot.",
  "Why did the real estate agent cross the road? To show the chicken a lovely 2-story coop.",
  "What kind of key opens a banana? A mon-key. (Sorry, wrong industry).",
  "Why are elevator jokes so good? They work on so many levels.",
  "What is a real estate agent’s favorite song? 'House of the Rising Sun'.",
  "Why did the house go to school? To improve its foundation.",
  "I would tell you a roof joke, but it might go over your head.",
  "Why did the window get arrested? It was framed.",
  "What’s a real estate agent’s favorite element? Radon... wait, no, Carbon.",
  "I’m reading a book on anti-gravity. It’s impossible to put down... just like this loan application.",
  "Why did the construction worker dip his finger in blue ink? To get a blueprint.",
  "How do you make a small fortune in real estate? Start with a large fortune.",
  "Why was the math book sad? Because it had too many problems (mostly DTI calculations).",
  "What did the wall say to the other wall? 'I'll meet you at the corner.'",
  "Why did the fence get promoted? It was outstanding in its field.",
  "What did the house wear to the party? Address.",
  "Why was the real estate agent checking the thermostat? To quell the heated market.",
  "Why did the tiny ghost join the football team? He was a little ghoul.",
  "What kind of room has no doors or windows? A mushroom.",
  "Why did the tree go to the dentist? To get a root canal.",
  "What’s the most expensive video game? Animal Crossing... have you seen those turnip prices?",
  "Why did the cookie go to the open house? Because he was a smart cookie.",
  "What did the traffic light say to the intersection? Don't look, I'm changing.",
  "Why are ghosts such bad liars? Because you can see right through them.",
  "What do you call a belt made of watches? A waist of time. (Unlike this chat, which is valuable!)",
  "Why did the computer go to the dentist? Because his bluetooth hurt.",
  "What do you call a factory that makes okay products? A satisfactory.",
  "Why couldn't the bicycle stand up by itself? It was two tired.",
  "What do you call a fake noodle? An impasta.",
  "How does a penguin build its house? Igloos it together.",
  "Why did the tomato turn red? Because it saw the salad dressing.",
  "What do you call a bear with no teeth? A gummy bear.",
  "Why did the golfer bring two pairs of pants? In case he got a hole in one.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why don't scientists trust atoms? Because they make up everything.",
  "What do you call a fish wearing a bowtie? Sofishticated.",
  "Why did the math teacher open the window? To clear up the problems.",
  "What do you call a pile of cats? A meowtain.",
  "Why did the banana go to the doctor? He wasn't peeling well.",
  "What do you call a pig that does karate? A pork chop.",
  "Why did the melon jump into the lake? It wanted to be a water-melon.",
  "What do you call a cow with no legs? Ground beef.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a duck who likes fireworks? A firequacker.",
  "Why did the crab never share? Because he was shellfish.",
  "What do you call a dog magician? A labracadabrador.",
  "Why did the cookie cry? Because his mom was a wafer in the army.",
  "What do you call a bear in the rain? A drizzly bear.",
  "Why did the picture go to jail? Because it was framed.",
  "What do you call a snobbish criminal going down stairs? A condescending con descending.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a sleeping dinosaur? A dino-snore.",
  "Why did the teacher wear sunglasses? Because her students were bright.",
  "What do you call a fast fungus? A mush-vroom.",
  "Why did the orange stop? It ran out of juice.",
  "What do you call a fly without wings? A walk.",
  "Why did the cookie go to the hospital? He felt crummy.",
  "What do you call a dinosaur with an extensive vocabulary? A thesaurus.",
  "Why did the gym close down? It just didn't work out.",
  "What do you call a boomerang that won't come back? A stick.",
  "Why did the scarecrow win an award? He was outstanding in his field.",
  "What do you call a bear with no ears? B.",
  "Why did the tomato blush? Because it saw the salad dressing.",
  "What do you call a fish without an eye? Fsh.",
  "Why did the golfer bring an extra pair of pants? In case he got a hole in one.",
  "What do you call a factory that sells good products? A satisfactory.",
  "Why don't skeletons fight each other? They don't have the guts.",
  "What do you call a fake noodle? An impasta.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why did the music teacher go to jail? For breaking the staff.",
  "What do you call a bear with no teeth? A gummy bear.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a pile of cats? A meowtain.",
  "Why did the banana go to the doctor? He wasn't peeling well.",
  "What do you call a pig that does karate? A pork chop.",
  "Why did the melon jump into the lake? It wanted to be a water-melon.",
  "What do you call a cow with no legs? Ground beef.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a duck who likes fireworks? A firequacker.",
  "Why did the crab never share? Because he was shellfish.",
  "What do you call a dog magician? A labracadabrador.",
  "Why did the cookie cry? Because his mom was a wafer in the army.",
  "What do you call a bear in the rain? A drizzly bear.",
  "Why did the picture go to jail? Because it was framed.",
  "What do you call a snobbish criminal going down stairs? A condescending con descending.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a sleeping dinosaur? A dino-snore.",
  "Why did the teacher wear sunglasses? Because her students were bright.",
  "What do you call a fast fungus? A mush-vroom.",
  "Why did the orange stop? It ran out of juice.",
  "What do you call a fly without wings? A walk.",
  "Why did the cookie go to the hospital? He felt crummy.",
  "What do you call a dinosaur with an extensive vocabulary? A thesaurus.",
  "Why did the gym close down? It just didn't work out.",
  "What do you call a boomerang that won't come back? A stick.",
  "Why did the scarecrow win an award? He was outstanding in his field.",
  "What do you call a bear with no ears? B.",
  "Why did the tomato blush? Because it saw the salad dressing.",
  "What do you call a fish without an eye? Fsh.",
  "Why did the golfer bring an extra pair of pants? In case he got a hole in one.",
  "What do you call a factory that sells good products? A satisfactory.",
  "Why don't skeletons fight each other? They don't have the guts.",
  "What do you call a fake noodle? An impasta.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why did the music teacher go to jail? For breaking the staff.",
  "What do you call a bear with no teeth? A gummy bear.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a pile of cats? A meowtain.",
  "Why did the banana go to the doctor? He wasn't peeling well.",
  "What do you call a pig that does karate? A pork chop.",
  "Why did the melon jump into the lake? It wanted to be a water-melon.",
  "What do you call a cow with no legs? Ground beef.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a duck who likes fireworks? A firequacker.",
  "Why did the crab never share? Because he was shellfish.",
  "What do you call a dog magician? A labracadabrador.",
  "Why did the cookie cry? Because his mom was a wafer in the army.",
  "What do you call a bear in the rain? A drizzly bear.",
  "Why did the picture go to jail? Because it was framed.",
  "What do you call a snobbish criminal going down stairs? A condescending con descending.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a sleeping dinosaur? A dino-snore.",
  "Why did the teacher wear sunglasses? Because her students were bright.",
  "What do you call a fast fungus? A mush-vroom.",
  "Why did the orange stop? It ran out of juice.",
  "What do you call a fly without wings? A walk.",
  "Why did the cookie go to the hospital? He felt crummy.",
  "What do you call a dinosaur with an extensive vocabulary? A thesaurus.",
  "Why did the gym close down? It just didn't work out.",
  "What do you call a boomerang that won't come back? A stick.",
  "Why did the scarecrow win an award? He was outstanding in his field.",
  "What do you call a bear with no ears? B.",
  "Why did the tomato blush? Because it saw the salad dressing.",
  "What do you call a fish without an eye? Fsh.",
  "Why did the golfer bring an extra pair of pants? In case he got a hole in one.",
  "What do you call a factory that sells good products? A satisfactory.",
  "Why don't skeletons fight each other? They don't have the guts.",
  "What do you call a fake noodle? An impasta.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why did the music teacher go to jail? For breaking the staff.",
  "What do you call a bear with no teeth? A gummy bear.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a pile of cats? A meowtain.",
  "Why did the banana go to the doctor? He wasn't peeling well.",
  "What do you call a pig that does karate? A pork chop.",
  "Why did the melon jump into the lake? It wanted to be a water-melon.",
  "What do you call a cow with no legs? Ground beef.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a duck who likes fireworks? A firequacker.",
  "Why did the crab never share? Because he was shellfish.",
  "What do you call a dog magician? A labracadabrador.",
  "What do you get when you cross a snowman and a vampire? Frostbite.",
  "Why did the bicycle fall over? It was two tired.",
  "Why did the tomato blush? Because it saw the salad dressing.",
  "What do you call a factory that sells good products? A satisfactory.",
  "Why don't skeletons fight each other? They don't have the guts.",
  "What do you call a fake noodle? An impasta.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why did the music teacher go to jail? For breaking the staff.",
  "What do you call a bear with no teeth? A gummy bear.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a pile of cats? A meowtain.",
  "Why did the banana go to the doctor? He wasn't peeling well.",
  "What do you call a pig that does karate? A pork chop.",
  "Why did the melon jump into the lake? It wanted to be a water-melon.",
  "What do you call a cow with no legs? Ground beef.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a duck who likes fireworks? A firequacker.",
  "Why did the crab never share? Because he was shellfish.",
  "What do you call a dog magician? A labracadabrador.",
  "Why did the cookie cry? Because his mom was a wafer in the army.",
  "What do you call a bear in the rain? A drizzly bear.",
  "Why did the picture go to jail? Because it was framed.",
  "What do you call a snobbish criminal going down stairs? A condescending con descending.",
  "Why did the chicken go to the seance? To get to the other side.",
  "What do you call a sleeping dinosaur? A dino-snore.",
  "Why did the teacher wear sunglasses? Because her students were bright.",
  "What do you call a fast fungus? A mush-vroom.",
  "Why did the orange stop? It ran out of juice.",
  "What do you call a fly without wings? A walk.",
  "Why did the cookie go to the hospital? He felt crummy.",
  "What do you call a dinosaur with an extensive vocabulary? A thesaurus.",
  "Why did the gym close down? It just didn't work out.",
  "What do you call a boomerang that won't come back? A stick.",
  "Why did the scarecrow win an award? He was outstanding in his field.",
  "What do you call a bear with no ears? B.",
  "Why did the tomato blush? Because it saw the salad dressing.",
  "What do you call a fish without an eye? Fsh.",
  "Why did the golfer bring an extra pair of pants? In case he got a hole in one.",
  "What do you call a factory that sells good products? A satisfactory.",
  "Why don't skeletons fight each other? They don't have the guts.",
  "What do you call a fake noodle? An impasta.",
  "Why did the bicycle fall over? It was two tired.",
  "What do you call a sleeping bull? A bulldozer.",
  "Why did the music teacher go to jail? For breaking the staff.",
  "What do you call a bear with no teeth? A gummy bear.",
];

const statusStyles = {
  Processing: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  Underwriting: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  "Action Required": "bg-rose-500/10 text-rose-300 border-rose-500/30",
  Approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Cleared to Close": "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Closing: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  Funded: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Fall out": "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

// localStorage key for persisting chat threads
const CHAT_STORAGE_KEY = 'cloudrealtor_chat_threads';

const LenderChat = ({ cases = [], caseFiles = {}, onOpenNewCase = () => { }, onUpdateCaseFiles = () => { }, isLoading = false }) => {
  const normalizedCases = useMemo(() => {
    // ... existing logic ...
    return cases.map((item) => {
      const normalized = normalizeCaseData(item);
      if (caseFiles && caseFiles[normalized.id]) {
        normalized.documents = Array.isArray(caseFiles[normalized.id])
          ? caseFiles[normalized.id]
          : [];
      } else if (!Array.isArray(normalized.documents)) {
        normalized.documents = [];
      }
      return normalized;
    });
  }, [cases, caseFiles]);

  const [activeCaseId, setActiveCaseId] = useState("");
  const [caseSearch, setCaseSearch] = useState("");
  const [threads, setThreads] = useState({});
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [activeTab, setActiveTab] = useState("case"); // "case" | "doc"
  const [activeDocId, setActiveDocId] = useState(null);
  const [showDocPreview, setShowDocPreview] = useState(true);
  const [docSearch, setDocSearch] = useState("");
  const [docSignedUrls, setDocSignedUrls] = useState({});
  const [showFullScreenPdf, setShowFullScreenPdf] = useState(false);
  const [aiReportsCount, setAiReportsCount] = useState({});  // { caseId: count }
  const [thinkingSteps, setThinkingSteps] = useState([]);  // Thinking steps for V2 API
  const [hasResponseStarted, setHasResponseStarted] = useState(false);  // Collapse thinking when response starts
  const hasResponseStartedRef = useRef(false);  // Ref for checking in callbacks
  const [sessionId] = useState(() => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback UUID v4 generator
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  });
  const endRef = useRef(null);

  // Streaming buffer refs
  const streamBufferRef = useRef("");
  const streamingMessageIdRef = useRef(null);
  const streamIntervalRef = useRef(null);
  const activeThreadKeyRef = useRef(null);

  // Thread Key helper
  const getThreadKey = () => {
    if (activeTab === 'case') return activeCaseId;
    if (activeTab === 'doc' && activeDocId) return `${activeCaseId}_doc_${activeDocId}`;
    return null;
  };

  // Load threads from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to proper format if needed
        Object.keys(parsed).forEach(key => {
          if (Array.isArray(parsed[key])) {
            parsed[key] = parsed[key].map(msg => ({
              ...msg,
              timestamp: msg.timestamp || 'Just now'
            }));
          }
        });
        setThreads(parsed);
      }
    } catch (error) {
      console.error('Failed to load chat threads from localStorage:', error);
    } finally {
      setThreadsLoaded(true);
    }
  }, []);

  // Save threads to localStorage whenever they change
  useEffect(() => {
    if (!threadsLoaded) return; // Don't save before initial load completes
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(threads));
    } catch (error) {
      console.error('Failed to save chat threads to localStorage:', error);
    }
  }, [threads, threadsLoaded]);

  const activeCase = normalizedCases.find((item) => item.id === activeCaseId);
  const filteredCases = normalizedCases.filter((item) => {
    const haystack = `${item.name} ${item.caseNumber} ${item.propertyAddress}`.toLowerCase();
    return haystack.includes(caseSearch.trim().toLowerCase());
  });

  // Debug logging removed for cleaner console output
  // To re-enable debugging, uncomment the useEffect below
  /*
  useEffect(() => {
    if (activeCaseId) {
      console.log("=== LenderChat Debug Info ===");
      console.log("Active Case ID:", activeCaseId);
      console.log("Active Case Found:", activeCase);
      console.log("Active Case Docs:", activeCase?.documents);
      console.log("Active Case Docs Length:", activeCase?.documents?.length);
      console.log("CaseFiles Prop:", caseFiles);
      console.log("CaseFiles Keys:", Object.keys(caseFiles || {}));
      console.log("CaseFiles for Active Case:", caseFiles?.[activeCaseId]);
      console.log("All Normalized Cases:", normalizedCases.map(c => ({
        id: c.id,
        name: c.name,
        docCount: c.documents?.length || 0
      })));

      if (activeCase && (!activeCase.documents || activeCase.documents.length === 0)) {
        console.warn("⚠️ Active case has no documents. Checking caseFiles for match...");
        if (caseFiles && caseFiles[activeCaseId]) {
          console.log("✅ Found matching docs in caseFiles:", caseFiles[activeCaseId]);
        } else {
          console.log("❌ No docs in caseFiles for this ID either.");
        }
      } else if (activeCase?.documents?.length > 0) {
        console.log("✅ Documents found:", activeCase.documents.length, "files");
      }
      console.log("=== End Debug Info ===");
    }
  }, [activeCaseId, activeCase, caseFiles, normalizedCases]);
  */


  const currentThreadKey = getThreadKey();

  // Smooth streaming effect loop
  useEffect(() => {
    streamIntervalRef.current = setInterval(() => {
      // Logic for 60fps smooth text rendering from buffer
      if (streamBufferRef.current.length > 0 && streamingMessageIdRef.current) {
        // Dynamic speed based on buffer backlog
        // If buffer has >100 chars, speed up significantly to catch up
        const backlog = streamBufferRef.current.length;
        const speed = backlog > 100 ? 5 : (backlog > 30 ? 3 : 1);

        const nextChunk = streamBufferRef.current.slice(0, speed);
        streamBufferRef.current = streamBufferRef.current.slice(speed);

        setThreads((prev) => {
          if (!streamingMessageIdRef.current) return prev;

          // Optimistically update the thread where the message lives
          const targetKey = activeThreadKeyRef.current || currentThreadKey;
          if (!targetKey || !prev[targetKey]) return prev;

          const updated = prev[targetKey].map((msg) => {
            if (msg.id === streamingMessageIdRef.current) {
              return {
                ...msg,
                content: msg.content + nextChunk,
                isStreaming: true
              };
            }
            return msg;
          });

          return { ...prev, [targetKey]: updated };
        });
      }
    }, 16); // ~60fps

    return () => clearInterval(streamIntervalRef.current);
  }, [currentThreadKey]);

  useEffect(() => {
    if (!activeCaseId && normalizedCases.length) {
      setActiveCaseId(normalizedCases[0].id);
    }
  }, [activeCaseId, normalizedCases]);

  // Fetch full case data with documents if not already loaded
  useEffect(() => {
    const loadCaseDocuments = async () => {
      if (!activeCaseId) return;

      const currentCase = normalizedCases.find(c => c.id === activeCaseId);
      if (!currentCase) return;

      // If case already has documents, no need to fetch
      if (currentCase.documents && currentCase.documents.length > 0) {
        console.log("✅ Case already has documents loaded:", currentCase.documents.length);
        return;
      }

      console.log("📥 Fetching full case data for:", activeCaseId);

      try {
        const response = await fetchCaseById(activeCaseId);
        const freshCaseData = response?.data?.data || response?.data || {};

        console.log("📥 Fetched case data:", freshCaseData);
        console.log("📥 Documents in response:", freshCaseData.documents);

        if (freshCaseData.documents && freshCaseData.documents.length > 0) {
          // Update the normalized cases with the fetched documents
          const updatedCase = normalizeCaseData(freshCaseData, currentCase);
          console.log("✅ Updated case with documents:", updatedCase.documents.length);


          // Update parent's caseFiles state with the fetched documents
          onUpdateCaseFiles((prev) => ({
            ...prev,
            [activeCaseId]: updatedCase.documents
          }));
        }
      } catch (error) {
        console.error("❌ Failed to fetch case documents:", error);
      }
    };

    loadCaseDocuments();
  }, [activeCaseId, normalizedCases]);

  // Fetch AI reports count for the active case
  useEffect(() => {
    const loadAiReports = async () => {
      if (!activeCaseId) return;

      // Skip if we already fetched for this case
      if (aiReportsCount[activeCaseId] !== undefined) return;

      try {
        const response = await fetchCaseRuns({ caseId: activeCaseId, limit: 50 });
        const executions = response?.data?.executions || [];
        // Count only successful/completed reports
        const completedReports = executions.filter(
          (exec) => exec.status === 'SUCCEEDED' || exec.status === 'COMPLETED'
        );
        setAiReportsCount((prev) => ({
          ...prev,
          [activeCaseId]: completedReports.length
        }));
      } catch (error) {
        console.error("Failed to fetch AI reports:", error);
        setAiReportsCount((prev) => ({
          ...prev,
          [activeCaseId]: 0
        }));
      }
    };

    loadAiReports();
  }, [activeCaseId, aiReportsCount]);

  useEffect(() => {
    if (!activeCaseId) return;
    setThreads((prev) => {
      if (prev[activeCaseId]) return prev;

      const caseData = normalizedCases.find((c) => c.id === activeCaseId);
      if (!caseData) return prev;

      const caseDetails = `📋 **Case Overview**

**Borrower:** ${caseData.name}
**Case Number:** ${caseData.caseNumber}
**Property:** ${caseData.propertyAddress || "Not specified"}
**Loan Type:** ${caseData.loanType}
**Loan Amount:** ${formatLoanAmount(caseData.loanAmount)}
**Status:** ${caseData.status}
**Documents:** ${caseData.documents?.length || 0} uploaded
**AI Reports:** ${aiReportsCount[activeCaseId] ?? caseData.aiReports?.length ?? 0} generated

Ask me anything about this case and I'll pull answers from the full file.`;

      const seeded = [
        {
          id: `seed-${activeCaseId}`,
          role: "assistant",
          content: caseDetails,
          timestamp: "Just now",
        },
      ];
      return { ...prev, [activeCaseId]: seeded };
    });
  }, [activeCaseId, normalizedCases]);

  // Update the seeded message when AI reports count is fetched
  useEffect(() => {
    if (!activeCaseId) return;
    const count = aiReportsCount[activeCaseId];
    if (count === undefined) return; // Not yet fetched

    setThreads((prev) => {
      const currentThread = prev[activeCaseId];
      if (!currentThread || currentThread.length === 0) return prev;

      // Find and update the seeded message
      const seedMessage = currentThread.find((msg) => msg.id === `seed-${activeCaseId}`);
      if (!seedMessage) return prev;

      // Check if already has correct count (avoid infinite loop)
      if (seedMessage.content.includes(`**AI Reports:** ${count} generated`)) {
        return prev;
      }

      const caseData = normalizedCases.find((c) => c.id === activeCaseId);
      if (!caseData) return prev;

      const updatedContent = `📋 **Case Overview**

**Borrower:** ${caseData.name}
**Case Number:** ${caseData.caseNumber}
**Property:** ${caseData.propertyAddress || "Not specified"}
**Loan Type:** ${caseData.loanType}
**Loan Amount:** ${formatLoanAmount(caseData.loanAmount)}
**Status:** ${caseData.status}
**Documents:** ${caseData.documents?.length || 0} uploaded
**AI Reports:** ${count} generated

Ask me anything about this case and I'll pull answers from the full file.`;

      const updatedThread = currentThread.map((msg) =>
        msg.id === `seed-${activeCaseId}`
          ? { ...msg, content: updatedContent }
          : msg
      );

      return { ...prev, [activeCaseId]: updatedThread };
    });
  }, [activeCaseId, aiReportsCount, normalizedCases]);

  // Fetch signed URL for selected document
  useEffect(() => {
    const fetchDocUrl = async () => {
      if (!activeDocId || !activeCase) return;

      // Check if we already have the URL cached
      if (docSignedUrls[activeDocId]) {
        console.log("✅ Using cached signed URL for:", activeDocId);
        return;
      }

      // Find the document
      const doc = activeCase.documents?.find((d, idx) => {
        const docId = d.id || d._id || `doc-${activeCaseId}-${idx}`;
        return docId === activeDocId;
      });

      if (!doc) {
        console.warn("⚠️ Document not found:", activeDocId);
        return;
      }

      // If document already has a URL, use it
      if (doc.signedUrl || doc.previewUrl || doc.downloadUrl) {
        console.log("✅ Document already has URL");
        return;
      }

      // Fetch signed URL using objectKey
      if (doc.objectKey) {
        console.log("📥 Fetching signed URL for objectKey:", doc.objectKey);
        try {
          const response = await fetchSignedUrl(doc.objectKey);
          const url = response?.data?.url || response?.data?.signedUrl || response?.url || response?.signedUrl;

          if (url) {
            console.log("✅ Got signed URL:", url);
            setDocSignedUrls(prev => ({
              ...prev,
              [activeDocId]: url
            }));
          } else {
            console.error("❌ No URL in response:", response);
          }
        } catch (error) {
          console.error("❌ Failed to fetch signed URL:", error);
        }
      } else {
        console.warn("⚠️ Document has no objectKey:", doc);
      }
    };

    fetchDocUrl();
  }, [activeDocId, activeCase, activeCaseId, docSignedUrls]);

  // Seed Doc Chat Context
  useEffect(() => {
    if (activeTab !== 'doc' || !activeCaseId || !activeDocId) return;
    const threadKey = `${activeCaseId}_doc_${activeDocId}`;

    setThreads(prev => {
      if (prev[threadKey]) return prev;

      const doc = activeCase?.documents?.find((d, idx) => {
        const docId = d.id || d._id || `doc-${activeCaseId}-${idx}`;
        return docId === activeDocId;
      });
      const docDetails = `📄 **Document Context**

**File:** ${doc?.name || 'Unknown Document'}
**Type:** ${doc?.type || 'N/A'}

I've analyzed this specific document. Should we review key clauses, anomalies, or summaries?`;

      return {
        ...prev,
        [threadKey]: [{
          id: `seed-${threadKey}`,
          role: "assistant",
          content: docDetails,
          timestamp: "Just now",
        }]
      };
    });
  }, [activeTab, activeCaseId, activeDocId, activeCase]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [threads, activeCaseId]);

  const currentThread = currentThreadKey && threads[currentThreadKey] ? threads[currentThreadKey] : [];

  const handleSend = async (textOverride = null) => {
    // Determine the text to send: either override or current input
    const textToSend = typeof textOverride === "string" ? textOverride : messageInput;

    if (!textToSend.trim() || !activeCaseId) return;
    if (activeTab === 'doc' && !activeDocId) return;

    const userText = textToSend.trim();
    const tempUserId = `user-${Date.now()}`;
    const newMessage = {
      id: tempUserId,
      role: "user",
      content: userText,
      timestamp: "Just now",
    };

    const tempBotId = `assistant-${Date.now() + 1}`;
    const placeholderReply = {
      id: tempBotId,
      role: "assistant",
      content: "",
      timestamp: "Just now",
      placeholder: false,
      isStreaming: true,
      loadingJoke: realEstateJokes[Math.floor(Math.random() * realEstateJokes.length)]
    };

    // Determine target thread key
    const targetKey = getThreadKey();
    if (!targetKey) return;

    setThreads((prev) => {
      const thread = prev[targetKey] || [];
      return { ...prev, [targetKey]: [...thread, newMessage, placeholderReply] };
    });
    setMessageInput("");

    // Initialize streaming state
    streamBufferRef.current = "";
    streamingMessageIdRef.current = tempBotId;
    activeThreadKeyRef.current = targetKey;

    // Clear thinking steps for new message
    setThinkingSteps([]);
    setHasResponseStarted(false);
    hasResponseStartedRef.current = false;

    // Debug logging - show all chat context
    const chatPayload = {
      message: userText,
      session_id: sessionId,
      case_id: activeCaseId,
      doc_id: activeTab === 'doc' ? activeDocId : undefined
    };
    console.log('[LenderChat] Preparing chat request:');
    console.log('[LenderChat] - activeTab:', activeTab);
    console.log('[LenderChat] - activeCaseId:', activeCaseId);
    console.log('[LenderChat] - activeDocId:', activeDocId);
    console.log('[LenderChat] - sessionId:', sessionId);
    console.log('[LenderChat] - targetKey:', targetKey);
    console.log('[LenderChat] - payload:', JSON.stringify(chatPayload, null, 2));

    // Use V2 streaming API with thinking steps
    sendStreamingChatQueryV2(
      chatPayload,
      {
        // onChunk - Push to buffer and collapse thinking steps
        onChunk: (chunk) => {
          streamBufferRef.current += chunk;
          // Collapse thinking steps once response starts streaming
          if (!hasResponseStartedRef.current) {
            hasResponseStartedRef.current = true;
            setHasResponseStarted(true);
          }
        },

        // onThinkingStep - Update thinking steps display
        onThinkingStep: (step) => {
          setThinkingSteps((prev) => {
            const existing = prev.find((s) => s.stepId === step.stepId);
            if (existing) {
              // Update existing step (e.g., in_progress → completed)
              return prev.map((s) =>
                s.stepId === step.stepId ? step : s
              );
            }
            // Add new step
            return [...prev, step];
          });
        },

        // onToolCall - Optional: could show tool usage
        onToolCall: (name, args) => {
          console.log('[LenderChat] Tool called:', name, args);
        },

        // onToolResult - Optional: could show tool results
        onToolResult: (name, result) => {
          console.log('[LenderChat] Tool result:', name, result?.substring(0, 100));
        },

        // onComplete
        onComplete: () => {
          // We do NOT turn off isStreaming immediately.
          // We wait for the buffer to empty.
          const checkDoneParams = setInterval(() => {
            if (streamBufferRef.current.length === 0) {
              clearInterval(checkDoneParams);
              streamingMessageIdRef.current = null;
              activeThreadKeyRef.current = null;

              setThreads((prev) => {
                const thread = prev[targetKey] || [];
                const updated = thread.map((msg) => {
                  if (msg.id === tempBotId) {
                    return { ...msg, isStreaming: false };
                  }
                  return msg;
                });
                return { ...prev, [targetKey]: updated };
              });

              // Clear thinking steps after response is complete
              setTimeout(() => setThinkingSteps([]), 500);
            }
          }, 100);
        },

        // onError
        onError: (error) => {
          console.error("Chat streaming error:", error);
          streamingMessageIdRef.current = null;
          activeThreadKeyRef.current = null;
          streamBufferRef.current = "";
          setThinkingSteps([]);

          setThreads((prev) => {
            const thread = prev[targetKey] || [];
            const updated = thread.map((msg) => {
              if (msg.id === tempBotId) {
                return {
                  ...msg,
                  content: msg.content + `\n\n[Error: ${error}]`,
                  isStreaming: false,
                  isError: true
                };
              }
              return msg;
            });
            return { ...prev, [targetKey]: updated };
          });
        }
      }
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6 h-full animate-pulse">
        <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5 flex flex-col h-full">
          <div className="flex justify-between items-center mb-6">
            <div className="h-4 w-24 bg-slate-800 rounded"></div>
            <div className="h-6 w-12 bg-slate-800 rounded-full"></div>
          </div>
          <div className="h-10 w-full bg-slate-800 rounded-xl mb-4"></div>
          <div className="space-y-3 flex-1">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 w-full bg-slate-800/50 rounded-xl"></div>
            ))}
          </div>
        </section>
        <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 flex flex-col h-full">
          <div className="h-16 border-b border-slate-800 flex items-center px-6 gap-4">
            <div className="h-8 w-8 bg-slate-800 rounded-full"></div>
            <div className="h-4 w-32 bg-slate-800 rounded"></div>
          </div>
          <div className="flex-1 p-6 space-y-4">
            <div className="flex gap-4">
              <div className="h-8 w-8 bg-slate-800 rounded-full"></div>
              <div className="h-20 w-2/3 bg-slate-800 rounded-2xl rounded-tl-none"></div>
            </div>
            <div className="flex gap-4 flex-row-reverse">
              <div className="h-8 w-8 bg-slate-800 rounded-full"></div>
              <div className="h-12 w-1/2 bg-slate-800 rounded-2xl rounded-tr-none"></div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-800">
            <div className="h-12 w-full bg-slate-800 rounded-xl"></div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6 h-full">
      <section className="relative overflow-hidden rounded-2xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900/60 bg-white p-5 flex flex-col h-full shadow-sm">
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full dark:bg-yellow-500/10 bg-yellow-500/5 blur-3xl" />
        <div className="relative flex-1 overflow-y-auto min-h-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] dark:text-slate-500 text-slate-400 font-semibold">Case context</p>
              <h3 className="text-lg font-semibold dark:text-white text-slate-900">Your active cases</h3>
            </div>
            <span className="text-xs dark:text-slate-400 text-slate-500 border dark:border-slate-700 border-slate-200 rounded-full px-2 py-1 bg-slate-100 dark:bg-transparent">Live</span>
          </div>

          <label className="text-xs dark:text-slate-400 text-slate-500 font-medium">Switch case</label>
          <div className="mt-2 relative">
            <select
              value={activeCaseId}
              onChange={(event) => {
                setActiveCaseId(event.target.value);
                setActiveTab("case");
                setActiveDocId(null);
                setShowDocPreview(true);
              }}
              className="w-full appearance-none rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-slate-50 px-3 py-2 pr-8 text-sm dark:text-slate-200 text-slate-700 focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/10"
            >
              {normalizedCases.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {item.caseNumber}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 dark:text-slate-500 text-slate-400" />
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 rounded-xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900 bg-slate-50 px-3 py-2 text-sm dark:text-slate-300 text-slate-600">
              <Search className="h-4 w-4 dark:text-slate-500 text-slate-400" />
              <input
                value={caseSearch}
                onChange={(event) => setCaseSearch(event.target.value)}
                placeholder="Search cases"
                className="w-full bg-transparent text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-4 space-y-3 max-h-64 overflow-auto pr-1">
            {filteredCases.length === 0 && (
              <div className="rounded-xl border border-dashed dark:border-slate-700 border-slate-300 px-3 py-4 text-sm dark:text-slate-500 text-slate-400">
                No cases match that search.
              </div>
            )}
            {filteredCases.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveCaseId(item.id);
                  setActiveTab("case");
                  setActiveDocId(null);
                  setShowDocPreview(true);
                }}
                className={`w-full text-left rounded-xl border px-3 py-3 transition ${item.id === activeCaseId
                  ? "dark:border-yellow-500/40 border-yellow-500/30 dark:bg-yellow-500/10 bg-yellow-50 dark:text-white text-yellow-900 shadow-sm"
                  : "dark:border-slate-800 border-slate-200 dark:bg-slate-900/60 bg-white dark:text-slate-300 text-slate-600 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{item.name}</p>
                    <p className="text-xs dark:text-slate-500 text-slate-400">{item.propertyAddress || "Property not set"}</p>
                  </div>
                  <span
                    className={`text-[11px] rounded-full border px-2 py-0.5 ${statusStyles[item.status] || "dark:border-slate-700 border-slate-200 dark:text-slate-400 text-slate-500 dark:bg-slate-800/40 bg-slate-100"
                      }`}
                  >
                    {item.status}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {activeCase ? (
            <div className="mt-5 rounded-2xl border dark:border-slate-800 border-slate-200 dark:bg-slate-950/70 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-xs dark:text-slate-400 text-slate-500 font-medium">
                <FileText className="h-4 w-4 dark:text-slate-500 text-slate-400" />
                Case snapshot
              </div>
              <div className="mt-3 space-y-2 text-sm dark:text-slate-200 text-slate-700">
                <div className="flex justify-between">
                  <span className="dark:text-slate-500 text-slate-500">Loan amount</span>
                  <span>{formatLoanAmount(activeCase.loanAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Loan type</span>
                  <span>{activeCase.loanType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Docs uploaded</span>
                  <span>{activeCase.documents?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">AI reports</span>
                  <span>{aiReportsCount[activeCaseId] ?? activeCase.aiReports?.length ?? 0}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-4 text-sm text-slate-400">
              <p>No cases yet.</p>
              <button
                type="button"
                onClick={onOpenNewCase}
                className="mt-3 w-full rounded-lg bg-yellow-600 px-3 py-2 text-sm font-semibold text-white hover:bg-yellow-500"
              >
                Create a case
              </button>
            </div>
          )}

          <div className="mt-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Suggested prompts</p>
            <div className="mt-3 space-y-2">
              {promptSuggestions.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => handleSend(prompt)}
                  className="w-full text-left rounded-xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900/80 bg-white px-3 py-2 text-xs dark:text-slate-300 text-slate-700 dark:hover:border-slate-700 hover:border-yellow-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden rounded-2xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900/60 bg-white flex flex-col h-full shadow-sm">
        <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full dark:bg-indigo-500/10 bg-indigo-500/5 blur-3xl" />

        {/* Header with Tabs */}
        <div className="relative border-b dark:border-slate-800 border-slate-200 px-6 py-4 dark:bg-slate-900/50 bg-slate-50">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-yellow-500/30 via-yellow-500/10 dark:to-slate-900/10 to-yellow-50 flex items-center justify-center border border-yellow-500/30">
                <Bot className="h-5 w-5 dark:text-yellow-300 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white text-slate-900">Case Assistant</h2>
                <p className="text-xs dark:text-slate-400 text-slate-600">
                  {activeCase ? `${activeCase.name} · ${activeCase.caseNumber}` : "Select a case to begin"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs dark:text-slate-400 text-slate-600">
              <span className="flex items-center gap-1 rounded-full border dark:border-slate-700 border-slate-300 px-2 py-1 dark:bg-transparent bg-white">
                <Sparkles className="h-3 w-3 text-yellow-400" />
                Live Context
              </span>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex p-1 dark:bg-slate-950/50 bg-slate-100 rounded-lg border dark:border-slate-800/50 border-slate-200 w-full sm:w-auto self-start">
            <button
              onClick={() => setActiveTab("case")}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "case"
                ? "dark:bg-slate-800 bg-white dark:text-white text-slate-900 shadow-sm border dark:border-slate-700 border-slate-300"
                : "dark:text-slate-400 text-slate-600 dark:hover:text-white hover:text-slate-900 dark:hover:bg-slate-800/50 hover:bg-white"
                }`}
            >
              <MessageSquare className="w-4 h-4" />
              Case Chat
            </button>
            {/* TODO: Chat with Doc feature - temporarily disabled
            <button
              onClick={() => setActiveTab("doc")}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "doc"
                ? "dark:bg-slate-800 bg-white dark:text-white text-slate-900 shadow-sm border dark:border-slate-700 border-slate-300"
                : "dark:text-slate-400 text-slate-600 dark:hover:text-white hover:text-slate-900 dark:hover:bg-slate-800/50 hover:bg-white"
                }`}
            >
              <FileText className="w-4 h-4" />
              Chat with Doc
            </button>
            */}
          </div>
        </div>

        {/* Chat Content or Doc Selection */}
        {/* Chat Content or Doc Selection */}
        <div className="flex-1 overflow-hidden flex flex-col relative">
          {activeTab === 'doc' && !activeDocId ? (
            // Document Selection View
            <div className="flex-1 overflow-y-auto px-6 py-6 animate-fade-in-up">
              <div className="text-center mb-8 mt-4">
                <h3 className="text-xl font-semibold dark:text-white text-slate-900 mb-2">Select a document to analyze</h3>
                <p className="dark:text-slate-400 text-slate-500 text-sm">Dive deep into any file. I can summarize, find clauses, or answer specific questions.</p>
              </div>

              {/* Document Search Bar */}
              {activeCase?.documents && activeCase.documents.length > 0 && (
                <div className="mb-6 max-w-md mx-auto">
                  <div className="flex items-center gap-2 rounded-xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900 bg-white px-3 py-2.5 text-sm dark:text-slate-300 text-slate-700">
                    <Search className="h-4 w-4 text-slate-500" />
                    <input
                      value={docSearch}
                      onChange={(e) => setDocSearch(e.target.value)}
                      placeholder="Search documents..."
                      className="w-full bg-transparent text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none"
                    />
                    {docSearch && (
                      <button
                        onClick={() => setDocSearch("")}
                        className="text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeCase?.documents && activeCase.documents.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeCase.documents
                    .filter((doc) => {
                      if (!docSearch.trim()) return true;
                      const searchLower = docSearch.toLowerCase();
                      return (
                        doc.name?.toLowerCase().includes(searchLower) ||
                        doc.type?.toLowerCase().includes(searchLower)
                      );
                    })
                    .map((doc, idx) => {
                      const docId = doc.id || doc._id || `doc-${activeCaseId}-${idx}`;
                      console.log(`📄 Rendering doc ${idx}:`, doc.name, "ID:", docId);

                      return (
                        <button
                          key={docId}
                          onClick={() => {
                            console.log("Document clicked:", doc);
                            setActiveDocId(docId);
                            setShowDocPreview(true);
                          }}
                          className="group flex flex-col items-start gap-3 p-4 rounded-xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900/40 bg-white hover:border-yellow-500/30 transition-all text-left shadow-sm"
                        >
                          <div className="p-2.5 rounded-lg dark:bg-slate-800 bg-slate-100 group-hover:bg-yellow-500/20 group-hover:text-yellow-400 dark:text-slate-400 text-slate-500 border dark:border-slate-700 border-slate-200 group-hover:border-yellow-500/30 transition-colors">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div className="w-full">
                            <p className="font-semibold dark:text-slate-200 text-slate-700 text-sm truncate w-full" title={doc.name}>{doc.name}</p>
                            <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{doc.type?.split('/')?.[1] || 'PDF'}</p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 rounded-xl border border-dashed dark:border-slate-800 border-slate-300 dark:bg-slate-900/20 bg-slate-50 text-slate-500">
                  <Files className="w-8 h-8 mb-3 opacity-50" />
                  <p>No documents found for this case.</p>
                </div>
              )}
            </div>
          ) : (
            // Chat View (Case or Doc)
            <div className="flex-1 flex flex-col h-full overflow-hidden">
              {activeTab === 'doc' && (
                <div className="shrink-0 flex items-center gap-3 px-6 py-3 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/95 bg-white/95 backdrop-blur z-20">
                  <button
                    onClick={() => setActiveDocId(null)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 transition-colors"
                    title="Back to documents"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-yellow-500" />
                      <p className="text-sm font-semibold dark:text-white text-slate-900 truncate">
                        {(() => {
                          const doc = activeCase?.documents?.find((d, idx) => {
                            const docId = d.id || d._id || `doc-${activeCaseId}-${idx}`;
                            return docId === activeDocId;
                          });
                          return doc?.name || 'Unknown Document';
                        })()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDocPreview(!showDocPreview)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${showDocPreview
                      ? "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30"
                      : "dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-600 dark:border-slate-700 border-slate-200 dark:hover:text-white hover:text-slate-900"
                      }`}
                  >
                    {showDocPreview ? (
                      <>
                        <EyeOff className="w-3.5 h-3.5" /> Hide Doc
                      </>
                    ) : (
                      <>
                        <Eye className="w-3.5 h-3.5" /> View Doc
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowFullScreenPdf(true)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-600 dark:border-slate-700 border-slate-200 dark:hover:text-white hover:text-slate-900 hover:border-yellow-500/30 transition-all"
                  >
                    <Maximize2 className="w-3.5 h-3.5" /> Full Screen
                  </button>
                </div>
              )}

              <div className="flex-1 flex flex-col lg:flex-row overflow-hidden absolute inset-0">
                {/* Document Preview Panel - Split View */}
                {activeTab === 'doc' && showDocPreview && (
                  <div className="w-full h-[40vh] lg:h-full lg:w-1/2 border-b lg:border-b-0 lg:border-r dark:border-slate-800 border-slate-200 dark:bg-slate-950 bg-slate-100 flex flex-col shrink-0">
                    {(() => {
                      const doc = activeCase?.documents?.find((d, idx) => {
                        const docId = d.id || d._id || `doc-${activeCaseId}-${idx}`;
                        return docId === activeDocId;
                      });

                      console.log("🖼️ PDF Viewer - activeDocId:", activeDocId);
                      console.log("🖼️ PDF Viewer - Found doc:", doc);
                      console.log("🖼️ PDF Viewer - Cached signed URL:", docSignedUrls[activeDocId]);
                      console.log("🖼️ PDF Viewer - signedUrl:", doc?.signedUrl);
                      console.log("🖼️ PDF Viewer - previewUrl:", doc?.previewUrl);
                      console.log("🖼️ PDF Viewer - downloadUrl:", doc?.downloadUrl);


                      // Determine effective URL - prioritize cached signed URL
                      const url = docSignedUrls[activeDocId] || doc?.signedUrl || doc?.previewUrl || doc?.downloadUrl;

                      if (!url) {
                        // Check if we're still fetching the URL
                        const isFetching = doc?.objectKey && !docSignedUrls[activeDocId];

                        console.warn("⚠️ No URL available for document preview", doc);
                        return (
                          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8 text-center">
                            {isFetching ? (
                              <>
                                <Loader2 className="w-12 h-12 mb-4 animate-spin text-yellow-500" />
                                <p>Loading document preview...</p>
                              </>
                            ) : (
                              <>
                                <FileText className="w-12 h-12 mb-4 opacity-20" />
                                <p>No preview available for this document.</p>
                                <p className="text-xs mt-2">Document: {doc?.name || 'Unknown'}</p>
                              </>
                            )}
                          </div>
                        );
                      }

                      if (doc.type?.includes("pdf") || url.endsWith(".pdf") || doc.name?.endsWith(".pdf") || url.startsWith("data:application/pdf")) {
                        return (
                          <iframe
                            src={url}
                            className="w-full h-full border-0"
                            title="PDF Document Viewer"
                            style={{ minHeight: '100%' }}
                          />
                        );
                      }

                      if (doc.type?.includes("image") || ["jpg", "jpeg", "png", "webp"].some(ext => doc.name.toLowerCase().endsWith(ext))) {
                        return (
                          <div className="flex-1 overflow-auto p-4 flex items-center justify-center dark:bg-slate-900/50 bg-slate-200/50">
                            <img src={url} alt="Preview" className="max-w-full max-h-full rounded shadow-lg object-contain" />
                          </div>
                        );
                      }

                      return (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8 text-center">
                          <p>Preview format not supported.</p>
                          <a href={url} target="_blank" rel="noreferrer" className="mt-4 text-yellow-400 hover:underline text-sm">Download File</a>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Chat Column */}
                <div className={`flex flex-col h-full dark:bg-slate-900/30 bg-slate-50 transition-all min-w-0 ${activeTab === 'doc' && showDocPreview ? 'lg:w-1/2 w-full' : 'w-full'}`}>
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    {currentThread.length === 0 && (
                      <div className="rounded-2xl border border-dashed dark:border-slate-700 border-slate-300 dark:bg-slate-950/40 bg-slate-100 p-6 text-center dark:text-slate-400 text-slate-500 mx-4 mt-8">
                        <p className="text-sm">Ask your first question to start the thread.</p>
                      </div>
                    )}

                    {currentThread.map((message, messageIndex) => (
                      <div key={message.id}>
                        <div
                          className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm shadow animate-scale-in origin-bottom-${message.role === "user" ? "right" : "left"} ${message.role === "user"
                              ? "bg-yellow-600 text-white"
                              : message.placeholder
                                ? "dark:bg-slate-900 bg-white dark:text-slate-400 text-slate-500 border border-dashed dark:border-slate-700 border-slate-300"
                                : "dark:bg-slate-800 bg-white dark:text-slate-100 text-slate-800 border dark:border-transparent border-slate-200 shadow-sm"
                              }`}
                          >
                            <div className="flex items-start gap-3">
                              {message.role === "assistant" && (
                                <div className={`mt-0.5 p-1.5 rounded-lg shrink-0 ${message.isStreaming ? "bg-yellow-500/20 shadow-[0_0_10px_rgba(14,165,233,0.3)]" : "dark:bg-slate-700/50 bg-slate-100"}`}>
                                  <Bot className={`h-4 w-4 ${message.isStreaming ? "text-yellow-400 animate-pulse" : "dark:text-slate-400 text-slate-500"}`} />
                                </div>
                              )}
                              {(message.isLoading || (message.isStreaming && !message.content)) ? (
                                <div className="flex flex-col gap-3 flex-1">
                                  {/* Thinking indicator with Brain icon */}
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      <div className="w-8 h-8 rounded-full dark:bg-yellow-500/20 bg-yellow-500/10 flex items-center justify-center animate-pulse">
                                        <Brain className="h-4 w-4 text-yellow-500" />
                                      </div>
                                      <div className="absolute inset-0 rounded-full dark:bg-yellow-500/10 bg-yellow-500/5 animate-ping" style={{ animationDuration: '2s' }} />
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-sm font-medium dark:text-slate-300 text-slate-600">Analyzing...</span>
                                      <span className="text-xs dark:text-slate-500 text-slate-400 italic">{message.loadingJoke || "Processing your request"}</span>
                                    </div>
                                  </div>
                                  {/* Thinking steps appear after the indicator */}
                                  {thinkingSteps.length > 0 && (
                                    <ThinkingSteps steps={thinkingSteps} isCollapsed={hasResponseStarted} />
                                  )}
                                </div>
                              ) : (
                                (() => {
                                  const { cleanContent, thinkingContent, actionButtons } = parseMessageContent(message.content);
                                  return (
                                    <div className="leading-relaxed flex-1 break-words min-w-0">
                                      {/* Collapsible thinking section */}
                                      {thinkingContent && (
                                        <details className="mb-3 rounded-lg dark:bg-slate-700/30 bg-slate-100 border dark:border-slate-600/50 border-slate-200">
                                          <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs dark:text-slate-400 text-slate-500 hover:dark:text-slate-300 hover:text-slate-600">
                                            <Brain className="h-3.5 w-3.5 text-yellow-500" />
                                            <span>View AI reasoning</span>
                                          </summary>
                                          <div className="px-3 pb-2 text-xs dark:text-slate-400 text-slate-500 whitespace-pre-wrap">
                                            {thinkingContent}
                                          </div>
                                        </details>
                                      )}

                                      {/* Main content */}
                                      <div className="whitespace-pre-wrap">
                                        {cleanContent.split('\n').map((line, i) => {
                                          const parts = line.split(/(\*\*.*?\*\*)/g);
                                          return (
                                            <div key={i} className="min-h-[1.2em]">
                                              {parts.map((part, j) => {
                                                if (part.startsWith('**') && part.endsWith('**')) {
                                                  return <strong key={j} className="dark:text-yellow-200 text-yellow-700">{part.slice(2, -2)}</strong>;
                                                }
                                                return <span key={j}>{part}</span>;
                                              })}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* Action buttons */}
                                      {actionButtons.length > 0 && !message.isStreaming && (
                                        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t dark:border-slate-700 border-slate-200">
                                          {actionButtons.map((btn, idx) => (
                                            <button
                                              key={idx}
                                              onClick={() => handleSend(btn)}
                                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                                idx === 0
                                                  ? 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-sm'
                                                  : 'dark:bg-slate-700 bg-slate-200 dark:text-slate-200 text-slate-700 dark:hover:bg-slate-600 hover:bg-slate-300 border dark:border-slate-600 border-slate-300'
                                              }`}
                                            >
                                              {btn}
                                            </button>
                                          ))}
                                        </div>
                                      )}

                                      {message.isStreaming && (
                                        <span className="inline-block w-2 h-4 ml-0.5 bg-yellow-400 animate-pulse" />
                                      )}
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={endRef} />
                  </div>

                  {/* Input Area */}
                  <div className="border-t dark:border-slate-800 border-slate-200 px-4 py-4 shrink-0 dark:bg-slate-900/80 bg-white/80 backdrop-blur">
                    <div className="flex items-end gap-3 rounded-2xl border dark:border-slate-800 border-slate-200 dark:bg-slate-950/70 bg-slate-50 px-4 py-3">
                      <textarea
                        value={messageInput}
                        onChange={(event) => setMessageInput(event.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        placeholder={
                          activeTab === 'doc' && !activeDocId
                            ? "Select a document above to chat..."
                            : activeCase ? "Ask anything..." : "Select a case..."
                        }
                        disabled={!activeCaseId || (activeTab === 'doc' && !activeDocId)}
                        className="flex-1 resize-none bg-transparent text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none disabled:cursor-not-allowed disabled:text-slate-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleSend()}
                        disabled={!messageInput.trim() || !activeCaseId || (activeTab === 'doc' && !activeDocId)}
                        className="flex items-center gap-2 rounded-xl bg-yellow-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-yellow-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" />
                        Send
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {activeTab === 'doc' ? (
                        <span className="inline-flex items-center gap-1 text-yellow-500">
                          <FileText className="h-3 w-3" />
                          Focusing on single document
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border dark:border-slate-800 border-slate-200 px-2 py-1">
                          <Sparkles className="h-3 w-3 text-slate-500" />
                          Searching entire case file
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Full Screen PDF Modal */}
      {showFullScreenPdf && activeDocId && (() => {
        const doc = activeCase?.documents?.find((d, idx) => {
          const docId = d.id || d._id || `doc-${activeCaseId}-${idx}`;
          return docId === activeDocId;
        });
        const url = docSignedUrls[activeDocId] || doc?.signedUrl || doc?.previewUrl || doc?.downloadUrl;

        return (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/90 backdrop-blur-sm"
            onClick={() => setShowFullScreenPdf(false)}
          >
            <div
              className="relative w-full max-w-6xl h-[95vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg border border-yellow-200">
                    <FileText className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-800">{doc?.name || 'Document'}</h3>
                    <p className="text-sm text-slate-500">Full Screen View</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFullScreenPdf(false)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 bg-white overflow-hidden">
                {url ? (
                  <iframe
                    src={url}
                    className="w-full h-full border-0"
                    title="PDF Full Screen Viewer"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 mb-4 animate-spin text-yellow-500 mx-auto" />
                      <p>Loading document...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LenderChat;
